import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import React, { useState, useCallback } from 'react';
import * as yup from 'yup';

import { Button } from '../../components';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WithdrawStatusReportDto } from '../../common/Types';
import { reportService } from '../../services/ReportService';
import moment from 'moment';

// Form data interface
interface WithdrawStatusReportFormData {
    startDate: string;
    endDate: string;
}

// Validation schema
const validationSchema = yup.object({
    startDate: yup.string().required('Start date is required'),
    endDate: yup
        .string()
        .required('End date is required')
        .test('date-range', 'End date must be after start date', function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            return moment(value).isSameOrAfter(moment(startDate));
        }),
});

const WithdrawStatusReport = () => {
    const [report, setReport] = useState<WithdrawStatusReportDto>();

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
    } = useForm<WithdrawStatusReportFormData>({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: {
            startDate: '',
            endDate: '',
        },
    });

    const watchedValues = watch();

    const clearSearchForm = useCallback(() => {
        setReport(undefined);
        // Reset form to default values
        const form = document.querySelector('form');
        if (form) {
            form.reset();
        }
    }, []);

    const onSubmit = useCallback((data: WithdrawStatusReportFormData) => {
        const toastId = toast.info('Processing, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .getWithdrawStatusReport(moment(data.startDate).format(), moment(data.endDate).format())
            .then((response) => {
                setReport(response);
                toast.update(toastId, {
                    render: 'Report generated successfully.',
                    type: 'success',
                    autoClose: 2500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Failed to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, []);

    const exportReport = useCallback(() => {
        if (!watchedValues.startDate || !watchedValues.endDate) {
            toast.error('Please select start and end dates before exporting');
            return;
        }

        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportWithdrawStatusReport(
                moment(watchedValues.startDate).format(),
                moment(watchedValues.endDate).format(),
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `VoluntaryWithdrawReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Unable to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [watchedValues.startDate, watchedValues.endDate]);

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Voluntary Withdraw Report</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="startDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Start Date:
                                    </Label>
                                    <Controller
                                        name="startDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    {errors.startDate && (
                                        <div className="text-danger small mt-1">{errors.startDate.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="endDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        End Date:
                                    </Label>
                                    <Controller
                                        name="endDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    {errors.endDate && (
                                        <div className="text-danger small mt-1">{errors.endDate.message}</div>
                                    )}
                                </div>
                                </div>

                                {/* Button row within the form */}
                                <Row className="mt-3">
                                    <Col className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Button
                                                type="submit"
                                                color="primary"
                                            >
                                                Search
                                            </Button>
                                            <Button
                                                type="button"
                                                color="link"
                                                onClick={clearSearchForm}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                        <Button
                                            type="button"
                                            color="info"
                                            onClick={exportReport}
                                            disabled={!watchedValues.startDate || !watchedValues.endDate}
                                        >
                                            Export Report
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                    <div className="mt-3">
                        <h4>Programs with Voluntary Withdraw Status</h4>
                    </div>
                </CardBody>
                <Table>
                    <thead>
                        <tr>
                            <th>Profession</th>
                            <th></th>
                            <th>Program</th>
                            <th></th>
                            <th>Institution Name</th>
                            <th>Program City</th>
                            <th>Program State</th>
                            <th>Effective Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report?.withdrawStatusDtos ? (
                            report.withdrawStatusDtos.map((record) => (
                                <tr key={record.programId}>
                                    <td>{record.professionTitle}</td>
                                    <td>{record.professionLevelTitle}</td>
                                    <td>{record.programId}</td>
                                    <td>{record.programName}</td>
                                    <td>{record.institutionTitle}</td>
                                    <td>{record.city}</td>
                                    <td>{record.state}</td>
                                    <td>{moment(record.withdrawEffectiveDate).format('MM/DD/YYYY')}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8}>No Results</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Total Records</th>
                            <th>{report?.totalCount}</th>
                        </tr>
                    </tfoot>
                </Table>
            </Card>
        </>
    );
};

export default WithdrawStatusReport;
